import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiAlertTriangle, FiRefreshCw, FiUser, FiClock, FiDollarSign,
  FiAlertCircle, FiShield, FiEye
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '@/components/SafeIcon';

interface AnomalyData {
  summary: {
    total_anomalies: number;
    critical: number;
    high: number;
    medium: number;
  };
  by_type: Record<string, number>;
  recent_anomalies: Array<{
    id: string;
    user_id: string | null;
    user_email: string | null;
    event_type: string;
    anomaly_type: string | null;
    anomaly_severity: string | null;
    plan_id: string | null;
    amount_usd: number | null;
    created_at: string;
  }>;
}

interface BillingAnomaliesPanelProps {
  onViewUser?: (userId: string) => void;
}

const BillingAnomaliesPanel: React.FC<BillingAnomaliesPanelProps> = ({ onViewUser }) => {
  const [data, setData] = useState<AnomalyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchAnomalies = async () => {
    setLoading(true);
    try {
      const { data: result, error } = await supabase.rpc('get_billing_anomalies', {
        p_days: days
      });

      if (error) throw error;
      if (result.success) {
        setData(result as AnomalyData);
      }
    } catch (err) {
      console.error('Error fetching anomalies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnomalies();
  }, [days]);

  const getSeverityColor = (severity: string | null) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20';
      case 'high': return 'text-orange-400 bg-orange-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getAnomalyIcon = (type: string | null) => {
    if (type?.includes('trial')) return FiUser;
    if (type?.includes('payment')) return FiDollarSign;
    return FiAlertTriangle;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
            <SafeIcon icon={FiShield} className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Billing Anomalies</h3>
            <p className="text-sm text-gray-400">Suspicious activity detection</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-1.5 rounded-lg bg-gray-700/50 border border-gray-600 text-sm text-gray-300"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          
          <button
            onClick={fetchAnomalies}
            className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className={`text-gray-300 ${loading ? 'animate-spin' : ''}`} size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <SafeIcon icon={FiRefreshCw} className="mx-auto text-gray-500 animate-spin" size={24} />
          <p className="mt-2 text-sm text-gray-400">Loading anomalies...</p>
        </div>
      ) : !data ? (
        <div className="p-8 text-center text-gray-500">Failed to load data</div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 p-6 border-b border-gray-700/50">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">{data.summary.total_anomalies}</p>
              <p className="text-xs text-gray-500 uppercase mt-1">Total</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-400">{data.summary.critical}</p>
              <p className="text-xs text-gray-500 uppercase mt-1">Critical</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-400">{data.summary.high}</p>
              <p className="text-xs text-gray-500 uppercase mt-1">High</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-400">{data.summary.medium}</p>
              <p className="text-xs text-gray-500 uppercase mt-1">Medium</p>
            </div>
          </div>

          {/* By Type */}
          {Object.keys(data.by_type).length > 0 && (
            <div className="px-6 py-4 border-b border-gray-700/50">
              <p className="text-xs text-gray-500 uppercase mb-3">By Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.by_type).map(([type, count]) => (
                  <span key={type} className="px-3 py-1 rounded-full bg-gray-700/50 text-sm text-gray-300">
                    {type.replace(/_/g, ' ')}: <span className="font-medium text-white">{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent Anomalies */}
          <div className="max-h-[400px] overflow-y-auto">
            {data.recent_anomalies.length === 0 ? (
              <div className="p-8 text-center">
                <SafeIcon icon={FiShield} className="mx-auto text-green-400" size={32} />
                <p className="mt-2 text-green-400">No anomalies detected</p>
                <p className="text-sm text-gray-500 mt-1">Everything looks normal</p>
              </div>
            ) : (
              data.recent_anomalies.map((anomaly, index) => {
                const Icon = getAnomalyIcon(anomaly.anomaly_type);
                
                return (
                  <motion.div
                    key={anomaly.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="px-6 py-4 border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getSeverityColor(anomaly.anomaly_severity)}`}>
                        <SafeIcon icon={Icon} size={18} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(anomaly.anomaly_severity)}`}>
                            {anomaly.anomaly_severity?.toUpperCase()}
                          </span>
                          <span className="text-sm font-medium text-white">
                            {anomaly.anomaly_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        
                        {anomaly.user_email && (
                          <p className="text-sm text-gray-400 mt-1">{anomaly.user_email}</p>
                        )}
                        
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span>{anomaly.event_type.replace(/_/g, ' ')}</span>
                          {anomaly.plan_id && <span>• {anomaly.plan_id}</span>}
                          {anomaly.amount_usd && <span>• ${anomaly.amount_usd}</span>}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <SafeIcon icon={FiClock} size={10} />
                          {formatTimeAgo(anomaly.created_at)}
                        </span>
                        {anomaly.user_id && onViewUser && (
                          <button
                            onClick={() => onViewUser(anomaly.user_id!)}
                            className="p-1.5 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                            title="View user timeline"
                          >
                            <SafeIcon icon={FiEye} className="text-gray-400" size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BillingAnomaliesPanel;
